import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Chip } from 'primereact/chip';
import { FileUpload } from 'primereact/fileupload';
import { useClassesStore } from '../../stateManagers/store/useClassesStore';
import { useSubjectsStore } from '../../stateManagers/store/useSubjectsStore';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import { useDefaultDataStore } from '../../stateManagers/store/useDefaultDataStore';
import { useSchoolDetailsStore } from '../../stateManagers/newStores/useSchoolDetailsStore';
import EditSubjectModal from './parts/EditSubjectModal';

interface Subject {
  id: string;
  subjectName: string;
  type: 'regular' | 'optional' | 'combined';
  mainSubjectId?: string | null;
  status: 'active' | 'inactive';
}

interface ClassSubjects {
  id: number;
  className: string;
  subjects: Subject[];
}

interface CsvPreviewItem {
  class_name: string;
  subject_name: string;
  subject_type: 'regular' | 'optional' | 'combined';
}

interface ClassPreviewGroup {
  class_name: string;
  subjects: CsvPreviewItem[];
}

interface NewSubjectRow {
  subject_name: string;
  subject_type: 'regular' | 'optional' | 'combined';
  main_subject_id: string;
}

type StatusFilter = 'all' | 'active' | 'inactive';

export default function SubjectsSetup() {
  const activeSchool = useDefaultDataStore(state => state.active_school);
  const { schoolDetails } = useSchoolDetailsStore();
  const { classes, loading: classesLoading, error: classesError, fetchClasses } = useClassesStore();

  const {
    subjects,
    loading: subjectsLoading,
    error: subjectsError,
    fetchSubjects,
    subjectsByClass,
    fetchAllSubjectsForClasses,
    loadingAll,
  } = useSubjectsStore();

  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [classesData, setClassesData] = useState<ClassSubjects[]>([]);
  const [uploading, setUploading] = useState(false);
  const [csvPreview, setCsvPreview] = useState<ClassPreviewGroup[]>([]);
  const [fileSelected, setFileSelected] = useState(false);

  // ── Add single subject(s) — mirrors "Add New Classes" pattern ──
  const [newSubjects, setNewSubjects] = useState<NewSubjectRow[]>([
    { subject_name: '', subject_type: 'regular', main_subject_id: '' },
  ]);
  const [addingSubject, setAddingSubject] = useState(false);

  // selection + bulk actions (students-list pattern)
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [bulkActing, setBulkActing] = useState(false);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [subjectsToEdit, setSubjectsToEdit] = useState<Subject[]>([]);

  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  // Same approach as SetupClassesAndSections
  const currentSession = schoolDetails?.current_session?.session || '';
  const currentSessionId = schoolDetails?.current_session?.id || '';

  const styles = `
    @keyframes fadeInSlide {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes bulkBarIn { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }
    .p-chip:hover {
      background: #d1e8ff !important;
      transition: background 0.2s ease;
    }
    .subject-row-checkbox {
      width: 16px;
      height: 16px;
      accent-color: #2855ae;
      cursor: pointer;
      flex-shrink: 0;
    }
    .status-filter-chip {
      padding: 6px 14px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      border: 1.5px solid #e5e7eb;
      background: #f3f4f6;
      color: #4b5563;
      transition: all 0.15s;
    }
    .status-filter-chip.active-filter {
      border-color: #2855ae;
      background: #e0f2fe;
      color: #0369a1;
    }
    .select-mode-btn {
      display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border-radius:10px;
      font-size:12px; font-weight:600; cursor:pointer; transition:all .2s; border:1.5px solid;
    }
    .select-mode-btn.off { background:white; color:#2855ae; border-color:#c7d2fe; }
    .select-mode-btn.off:hover { background:#eff6ff; }
    .select-mode-btn.on { background:#2855ae; color:white; border-color:#2855ae; }
    .bulk-action-bar {
      animation: bulkBarIn .22s ease;
      display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px;
      background: linear-gradient(135deg,#1e40af,#3b82f6);
      border-radius: 14px; padding: 12px 18px; margin-bottom: 16px;
      box-shadow: 0 4px 16px rgba(59,130,246,.3);
    }
    .bulk-count { font-size:13px; font-weight:700; color:white; }
    .bulk-count span { opacity:.75; font-weight:500; }
    .bulk-action-btn {
      display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:10px;
      font-size:12px; font-weight:600; cursor:pointer; border:none; transition:all .15s;
    }
    .bulk-action-btn:disabled { opacity: .5; cursor: not-allowed; }
    .bulk-action-btn.activate { background:#22c55e; color:white; }
    .bulk-action-btn.activate:hover:not(:disabled) { background:#16a34a; }
    .bulk-action-btn.deactivate { background:#f59e0b; color:white; }
    .bulk-action-btn.deactivate:hover:not(:disabled) { background:#d97706; }
    .bulk-action-btn.primary { background:white; color:#1e40af; }
    .bulk-action-btn.primary:hover:not(:disabled) { background:#eff6ff; }
    .bulk-action-btn.ghost { background:rgba(255,255,255,.15); color:white; border:1px solid rgba(255,255,255,.3); }
    .bulk-action-btn.ghost:hover:not(:disabled) { background:rgba(255,255,255,.25); }
    .status-switch {
      position: relative;
      width: 40px;
      height: 22px;
      border-radius: 999px;
      cursor: pointer;
      border: none;
      flex-shrink: 0;
      transition: background 0.2s;
      padding: 0;
    }
    .status-switch.on { background: #22c55e; }
    .status-switch.off { background: #d1d5db; }
    .status-switch:disabled { opacity: 0.6; cursor: not-allowed; }
    .status-switch-knob {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 18px;
      height: 18px;
      background: white;
      border-radius: 50%;
      transition: transform 0.2s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
    .status-switch.on .status-switch-knob { transform: translateX(18px); }
    .custom-file-upload .p-fileupload-buttonbar {
      background: transparent !important;
      border: none !important;
      padding: 0 !important;
      display: flex !important;
      gap: 12px !important;
      justify-content: center !important;
    }
    .custom-file-upload .p-button {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
      border: none !important;
      borderRadius: 12px !important;
      padding: 10px 20px !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3) !important;
      transition: all 0.2s !important;
    }
    .custom-file-upload .p-button:hover {
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
    }
    .custom-file-upload .p-fileupload-content {
      background: #f9fafb !important;
      border: 2px dashed #e5e7eb !important;
      border-radius: 12px !important;
      padding: 20px !important;
      margin-top: 12px !important;
    }
  `;

  const fetchClassesWithSchool = async () => {
    const schoolId = activeSchool?.school_id;
    if (schoolId && currentSessionId) {
      await fetchClasses(schoolId, currentSessionId);
    } else if (schoolId) {
      await fetchClasses(schoolId);
    } else {
      await fetchClasses();
    }
  };

  useEffect(() => {
    fetchClassesWithSchool();
  }, [activeSchool?.school_id, currentSessionId]);

  useEffect(() => {
    if (classes && classes.length > 0 && classesData.length === 0) {
      const initialClassesData = classes.map((classItem) => ({
        id: classItem.id,
        className: classItem.class_name,
        subjects: [],
      }));
      setClassesData(initialClassesData);
      setSelectedClassId(classes[0]?.id || null);
    }
  }, [classes, classesData.length]);

  useEffect(() => {
    if (selectedClassId) {
      fetchSubjects(selectedClassId);
      clearSelection();
    }
  }, [selectedClassId, fetchSubjects]);

  useEffect(() => {
    if (subjects && selectedClassId) {
      const allSubjects: Subject[] = [
        ...subjects.regular.map((item) => ({
          id: item.subject_id,
          subjectName: item.subject_name,
          type: item.subject_type as 'regular' | 'optional' | 'combined',
          mainSubjectId: item.main_subject_id ?? null,
          status: item.status,
        })),
        ...subjects.optional.map((item) => ({
          id: item.subject_id,
          subjectName: item.subject_name,
          type: item.subject_type as 'regular' | 'optional' | 'combined',
          mainSubjectId: null,
          status: item.status,
        })),
        ...subjects.combined.map((item) => ({
          id: item.subject_id,
          subjectName: item.subject_name,
          type: item.subject_type as 'regular' | 'optional' | 'combined',
          mainSubjectId: item.main_subject_id ?? null,
          status: item.status,
        })),
      ];

      setClassesData((prev) =>
        prev.map((classItem) =>
          classItem.id === selectedClassId ? { ...classItem, subjects: allSubjects } : classItem
        )
      );
    }
  }, [subjects, selectedClassId]);

  useEffect(() => {
    if (classesError) toast.current?.show({ severity: 'error', summary: 'Error', detail: classesError, life: 3000 });
    if (subjectsError) toast.current?.show({ severity: 'error', summary: 'Error', detail: subjectsError, life: 3000 });
  }, [classesError, subjectsError]);

  const clearSelection = () => setSelectedIds(new Set());

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (visibleSubjects: Subject[]) => {
    const visibleIds = visibleSubjects.map(s => s.id);
    const allChecked = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allChecked) {
        visibleIds.forEach(id => next.delete(id));
      } else {
        visibleIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    clearSelection();
  };

  // ── Add single subject(s) — row helpers, mirrors handleAddRow/handleRemoveRow/handleUpdateNewRow in SetupClassesAndSections ──
  const handleAddSubjectRow = () => {
    setNewSubjects((prev) => [...prev, { subject_name: '', subject_type: 'regular', main_subject_id: '' }]);
  };

  const handleRemoveSubjectRow = (index: number) => {
    setNewSubjects((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleUpdateNewSubjectRow = (
    index: number,
    field: 'subject_name' | 'subject_type' | 'main_subject_id',
    value: string
  ) => {
    setNewSubjects((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        if (field === 'subject_type') {
          const nextType = value as 'regular' | 'optional' | 'combined';
          // clear main_subject_id when switching away from "combined"
          return { ...row, subject_type: nextType, main_subject_id: nextType === 'combined' ? row.main_subject_id : '' };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const handleSaveNewSubjects = async () => {
    if (!selectedClassId) {
      toast.current?.show({ severity: 'warn', summary: 'No Class Selected', detail: 'Please select a class first.', life: 3000 });
      return;
    }
    if (!currentSessionId) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Current session not found. Please ensure a session is marked as "Current".', life: 3000 });
      return;
    }

    const trimmed = newSubjects.map((row) => ({
      subject_name: row.subject_name.trim(),
      subject_type: row.subject_type,
      main_subject_id: row.subject_type === 'combined' && row.main_subject_id ? row.main_subject_id : undefined,
    }));

    const invalid = trimmed.some((row) => !row.subject_name);
    if (invalid) {
      toast.current?.show({ severity: 'warn', summary: 'Missing Fields', detail: 'Subject name is required for every row.', life: 3000 });
      return;
    }

    setAddingSubject(true);
    try {
      const results = await Promise.allSettled(
        trimmed.map((row) => {
          const payload: any = {
            session_id: currentSessionId,
            class_id: String(selectedClassId),
            subject_name: row.subject_name,
            subject_type: row.subject_type,
          };
          if (row.main_subject_id) payload.main_subject_id = row.main_subject_id;
          if (activeSchool?.school_id) payload.school_id = activeSchool.school_id;
          if (currentSession) payload.session = currentSession;
          return apiClient.post('/subjects', payload);
        })
      );

      const successCount = results.filter((r) => r.status === 'fulfilled' && (r.value as any).data?.status === 'success').length;
      const errorCount = results.length - successCount;

      if (successCount > 0) {
        toast.current?.show({
          severity: 'success',
          summary: 'Added',
          detail: `${successCount} subject(s) created successfully.${errorCount > 0 ? ` ${errorCount} failed.` : ''}`,
          life: 3500,
        });
        setNewSubjects([{ subject_name: '', subject_type: 'regular', main_subject_id: '' }]);
        if (selectedClassId) fetchSubjects(selectedClassId);
      } else {
        toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'No subjects were created. Please check the rows.', life: 3000 });
      }

      if (errorCount > 0) {
        const messages = results
          .map((r) => {
            if (r.status === 'rejected') return r.reason?.response?.data?.message || 'Request failed';
            if (r.value.data?.status === 'error') return r.value.data?.message || 'Could not add subject';
            return null;
          })
          .filter(Boolean)
          .slice(0, 3)
          .join('; ');
        toast.current?.show({
          severity: 'warn',
          summary: 'Some Errors Occurred',
          detail: messages.length > 150 ? messages.substring(0, 150) + '...' : messages,
          life: 5000,
        });
      }
    } finally {
      setAddingSubject(false);
    }
  };

  const openEditModal = (subjectList: Subject[]) => {
    setSubjectsToEdit(subjectList);
    setEditModalVisible(true);
  };

  const handleEditSaved = () => {
    if (selectedClassId) fetchSubjects(selectedClassId);
    clearSelection();
  };

  // single-row instant activate/deactivate — no modal
  const handleToggleStatus = async (subject: Subject) => {
    const newStatus = subject.status === 'active' ? 'inactive' : 'active';
    setTogglingId(subject.id);

    // optimistic update
    setClassesData(prev =>
      prev.map(c =>
        c.id === selectedClassId
          ? { ...c, subjects: c.subjects.map(s => (s.id === subject.id ? { ...s, status: newStatus } : s)) }
          : c
      )
    );

    try {
      await apiClient.patch(`/subjects/${subject.id}/status`, { status: newStatus });
      toast.current?.show({
        severity: 'success',
        summary: newStatus === 'active' ? 'Activated' : 'Deactivated',
        detail: `"${subject.subjectName}" is now ${newStatus}.`,
        life: 2500,
      });
    } catch (error: any) {
      // revert on failure
      setClassesData(prev =>
        prev.map(c =>
          c.id === selectedClassId
            ? { ...c, subjects: c.subjects.map(s => (s.id === subject.id ? { ...s, status: subject.status } : s)) }
            : c
        )
      );
      toast.current?.show({
        severity: 'error',
        summary: 'Failed',
        detail: error.response?.data?.message || 'Could not update subject status',
        life: 3000,
      });
    } finally {
      setTogglingId(null);
    }
  };

  // bulk activate/deactivate
  const handleBulkStatusChange = async (newStatus: 'active' | 'inactive') => {
    if (selectedIds.size === 0) return;
    setBulkActing(true);
    const ids = Array.from(selectedIds);

    try {
      await apiClient.patch('/subjects/bulk-status', { subject_ids: ids, status: newStatus });
      setClassesData(prev =>
        prev.map(c =>
          c.id === selectedClassId
            ? { ...c, subjects: c.subjects.map(s => (selectedIds.has(s.id) ? { ...s, status: newStatus } : s)) }
            : c
        )
      );
      toast.current?.show({
        severity: 'success',
        summary: newStatus === 'active' ? 'Activated' : 'Deactivated',
        detail: `${ids.length} subject(s) updated.`,
        life: 3000,
      });
      clearSelection();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Bulk Update Failed',
        detail: error.response?.data?.message || 'Could not update subjects',
        life: 3000,
      });
    } finally {
      setBulkActing(false);
    }
  };

  // Used only to render the preview panel — the raw file is what actually gets uploaded.
  const handleFileSelect = (event: any) => {
    const file = event.files[0];
    if (!file) { setFileSelected(false); return; }
    setFileSelected(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        toast.current?.show({ severity: 'error', summary: 'Invalid CSV', detail: 'CSV file must contain at least a header and one data row', life: 3000 });
        return;
      }

      const classMap: { [key: string]: CsvPreviewItem[] } = {};
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const parts = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
        if (parts.length >= 2) {
          const className = parts[0]?.trim().replace(/"/g, '') || '';
          const subjectNames = parts[1]?.trim().replace(/"/g, '').split(',') || [];
          if (!classMap[className]) classMap[className] = [];
          subjectNames.forEach((subjectName) => {
            if (subjectName.trim()) {
              classMap[className].push({ class_name: className, subject_name: subjectName.trim(), subject_type: 'regular' });
            }
          });
        }
      }

      const previewData: ClassPreviewGroup[] = Object.keys(classMap).map(className => ({
        class_name: className,
        subjects: classMap[className],
      }));

      setCsvPreview(previewData);
      const totalSubjects = previewData.reduce((sum, group) => sum + group.subjects.length, 0);
      toast.current?.show({ severity: 'info', summary: 'Preview Ready', detail: `${previewData.length} class(es) with ${totalSubjects} subject(s) found in CSV`, life: 3000 });
    };
    reader.readAsText(file);
  };

  // ✅ SIMPLIFIED: send the CSV file straight through as-is (class_name, subject_names).
  // The backend already splits the comma-separated subject_names column itself,
  // so there's no need to re-parse/rebuild the CSV client-side before upload.
  const handleFileUpload = async (event: any) => {
    const file = event.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('csv_file', file);

    if (activeSchool?.school_id) {
      formData.append('school_id', activeSchool.school_id);
    }
    // Same approach as SetupClassesAndSections
    if (currentSessionId) {
      formData.append('session_id', currentSessionId);
    }
    if (currentSession) {
      formData.append('session', currentSession);
    }

    try {
      setUploading(true);
      const response = await apiClient.post('/subjects/bulk-csv', formData);

      if (response.data.status === 'success' && response.data.results) {
        const successCount = response.data.results.filter((item: any) => item.status === 'success').length;
        const errorCount = response.data.results.filter((item: any) => item.status === 'error').length;

        if (successCount > 0) {
          toast.current?.show({
            severity: 'success',
            summary: 'Upload Complete',
            detail: `${successCount} subject(s) added successfully!${errorCount > 0 ? ` ${errorCount} failed.` : ''}`,
            life: 4000,
          });
          setCsvPreview([]);
          setFileSelected(false);
          if (fileUploadRef.current) fileUploadRef.current.clear();
          setTimeout(() => { if (selectedClassId) fetchSubjects(selectedClassId); }, 500);
        } else {
          toast.current?.show({ severity: 'error', summary: 'Upload Failed', detail: 'No subjects were added. Please check your CSV file.', life: 3000 });
        }

        if (errorCount > 0) {
          const errors = response.data.results
            .filter((item: any) => item.status === 'error')
            .map((item: any) => item.message)
            .slice(0, 3)
            .join('; ');
          toast.current?.show({ severity: 'warn', summary: 'Some Errors Occurred', detail: errors.length > 150 ? errors.substring(0, 150) + '...' : errors, life: 5000 });
        }
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: 'Upload Failed', detail: error.response?.data?.message || 'Failed to upload CSV file', life: 3000 });
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = async () => {
    if (!classes || classes.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'No Classes', detail: 'No classes available to generate CSV.', life: 3000 });
      return;
    }

    const classIds = classes.map((c) => c.id);
    const uncachedIds = classIds.filter((id) => !subjectsByClass[id]);

    if (uncachedIds.length > 0) {
      toast.current?.show({ severity: 'info', summary: 'Preparing CSV', detail: 'Fetching subjects for all classes...', life: 2000 });
      await fetchAllSubjectsForClasses(uncachedIds);
    }

    const latestCache = useSubjectsStore.getState().subjectsByClass;

    const headers = 'class_name,subject_names';
    const sampleData = classes.map((classItem) => {
      const cached = latestCache[classItem.id];
      if (cached && cached.subjects.length > 0) {
        // ✅ FIX: quote the subject list so a comma-separated field survives
        // round-tripping through CSV parsers (including our own backend).
        const subjectNames = cached.subjects.map((s) => s.subject_name).join(', ');
        return `${classItem.class_name},"${subjectNames}"`;
      }
      return `${classItem.class_name},"Subject 1, Subject 2, Subject 3"`;
    });

    const csvContent = [headers, ...sampleData].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample_subjects.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.current?.show({ severity: 'success', summary: 'Download Started', detail: `sample_subjects.csv with ${classes.length} class(es) is downloading...`, life: 2000 });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'regular':  return { bg: '#dbeafe', text: '#1e40af' };
      case 'optional': return { bg: '#fef3c7', text: '#92400e' };
      case 'combined': return { bg: '#e0e7ff', text: '#4338ca' };
      default:         return { bg: '#f3f4f6', text: '#4b5563' };
    }
  };

  const getMainSubjectName = (mainSubjectId: string | null | undefined, allSubjects: Subject[]) => {
    if (!mainSubjectId) return null;
    return allSubjects.find(s => s.id === mainSubjectId)?.subjectName ?? null;
  };

  const selectedClass = classesData.find((c) => c.id === selectedClassId);
  const allClassSubjects = selectedClass?.subjects ?? [];
  const hasSubjects = allClassSubjects.length > 0;

  const visibleSubjects = allClassSubjects.filter((s) => {
    if (statusFilter === 'all') return true;
    return s.status === statusFilter;
  });

  const activeCount = allClassSubjects.filter(s => s.status === 'active').length;
  const inactiveCount = allClassSubjects.filter(s => s.status === 'inactive').length;

  const visibleIds = visibleSubjects.map(s => s.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  // for the bulk bar: are the selected subjects a mix, or can we say "activate all" / "deactivate all"
  const selectedSubjects = allClassSubjects.filter(s => selectedIds.has(s.id));
  const anySelectedInactive = selectedSubjects.some(s => s.status === 'inactive');
  const anySelectedActive = selectedSubjects.some(s => s.status === 'active');

  return (
    <div style={{ minHeight: '100vh', padding: '0px', background: '#fdfdfdff' }}>
      <style>{styles}</style>
      <Toast ref={toast} />

      <EditSubjectModal
        visible={editModalVisible}
        subjects={subjectsToEdit}
        classId={selectedClassId}
        onHide={() => { setEditModalVisible(false); setSubjectsToEdit([]); }}
        onSaved={handleEditSaved}
      />

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
        borderRadius: '24px',
        padding: '32px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '16px',
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <i className="pi pi-book" style={{ fontSize: '18px', color: 'white' }} />
          </div>
          <div>
            <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '600', margin: '0 0 4px 0' }}>
              Subjects Setup
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ background: 'rgba(74, 222, 128, 0.9)', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                {classes?.length || 0} Classes
              </span>
              {currentSession && (
                <span style={{ background: 'rgba(251,191,36,0.9)', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                  Session: {currentSession}
                </span>
              )}
              <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                {new Date().toISOString().split('T')[0]}
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={downloadSampleCSV}
          disabled={loadingAll}
          style={{
            background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.4)', borderRadius: '14px',
            padding: '10px 18px', cursor: loadingAll ? 'not-allowed' : 'pointer', color: 'white', fontSize: '13px',
            fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s',
            backdropFilter: 'blur(4px)', opacity: loadingAll ? 0.6 : 1,
          }}
          onMouseOver={(e) => { if (!loadingAll) e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
        >
          {loadingAll ? <i className="pi pi-spin pi-spinner" style={{ fontSize: '14px' }} /> : <i className="pi pi-download" style={{ fontSize: '14px' }} />}
          {loadingAll ? 'Preparing...' : 'Sample CSV'}
        </button>
      </div>

      {/* Subject Types Info */}
      {!hasSubjects && (
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)', border: '2px solid #dbeafe' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
            <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '32px', height: '32px' }}>
              <i className="pi pi-info-circle" style={{ fontSize: '14px', color: 'white' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: '600', margin: '0 0 8px 0' }}>Subject Types</h4>
              <div style={{ color: '#6b7280', fontSize: '13px', lineHeight: '1.6' }}>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', marginRight: '6px' }}>Regular</span>
                  Core subjects mandatory for all students
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', marginRight: '6px' }}>Optional</span>
                  Elective subjects students can choose from
                </div>
                <div>
                  <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', marginRight: '6px' }}>Combined</span>
                  Integrated subjects covering multiple topics
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Classes Chips */}
      {classesLoading ? (
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '2px solid #dbeafe', textAlign: 'center' }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '48px', color: '#2855ae' }} />
          <p style={{ color: '#6b7280', marginTop: '16px', fontSize: '16px', fontWeight: '500' }}>Loading classes...</p>
        </div>
      ) : classes && classes.length > 0 ? (
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '2px solid #dbeafe', animation: 'fadeInSlide 0.5s ease-out' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', padding: '8px', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="pi pi-graduation-cap" style={{ fontSize: '14px', color: 'white' }} />
            </div>
            <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: '600', margin: '0' }}>Available Classes</h4>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {classes.map((classItem) => (
              <Chip
                key={classItem.id}
                label={classItem.class_name}
                onClick={() => setSelectedClassId(classItem.id)}
                style={{
                  background: selectedClassId === classItem.id ? '#e0f2fe' : '#f3f4f6',
                  color: selectedClassId === classItem.id ? '#0369a1' : '#4b5563',
                  fontSize: '12px', fontWeight: '600', padding: '6px 12px', borderRadius: '12px',
                  border: selectedClassId === classItem.id ? '2px solid #2855ae' : '1px solid #e5e7eb',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '20px', padding: '48px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <i className="pi pi-inbox" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
          <h3 style={{ color: '#6b7280', fontSize: '18px', margin: '0 0 8px 0' }}>No Classes Found</h3>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>Classes will appear here once they are configured.</p>
        </div>
      )}

      {/* Add Single/Multiple Subjects — mirrors "Add New Classes" in SetupClassesAndSections */}
      {selectedClassId && (
        <div style={{ background: 'white', borderRadius: '20px', padding: '28px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', padding: '8px', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="pi pi-plus" style={{ fontSize: '14px', color: 'white' }} />
            </div>
            <h3 style={{ color: '#2855ae', fontSize: '16px', fontWeight: '600', margin: '0' }}>
              Add New Subjects to {selectedClass?.className}
            </h3>
          </div>

          {!currentSessionId && (
            <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '12px', border: '1px solid #fcd34d', marginBottom: '16px' }}>
              <p style={{ color: '#92400e', fontSize: '13px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="pi pi-exclamation-triangle" style={{ color: '#92400e' }} />
                No current session found. Please ensure a session is marked as "Current" in Session Management.
              </p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {newSubjects.map((row, index) => (
              <div
                key={index}
                style={{ background: '#f9fafb', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '16px' }}
              >
                <div style={{ minWidth: '40px', height: '40px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '14px', flexShrink: 0 }}>
                  {index + 1}
                </div>
                <div style={{ flex: 1, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '2', minWidth: '200px' }}>
                    <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Subject Name *</label>
                    <InputText
                      value={row.subject_name}
                      onChange={(e) => handleUpdateNewSubjectRow(index, 'subject_name', e.target.value)}
                      placeholder="e.g., Mathematics"
                      disabled={addingSubject}
                      style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                    />
                  </div>
                  <div style={{ flex: '1', minWidth: '160px' }}>
                    <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Type</label>
                    <select
                      value={row.subject_type}
                      onChange={(e) => handleUpdateNewSubjectRow(index, 'subject_type', e.target.value)}
                      disabled={addingSubject}
                      style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', background: 'white' }}
                    >
                      <option value="regular">Regular</option>
                      <option value="optional">Optional</option>
                      <option value="combined">Combined</option>
                    </select>
                  </div>
                  {row.subject_type === 'combined' && (
                    <div style={{ flex: '1', minWidth: '180px' }}>
                      <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Main Subject</label>
                      <select
                        value={row.main_subject_id}
                        onChange={(e) => handleUpdateNewSubjectRow(index, 'main_subject_id', e.target.value)}
                        disabled={addingSubject}
                        style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', background: 'white' }}
                      >
                        <option value="">Select subject...</option>
                        {allClassSubjects.filter(s => s.type !== 'combined').map((s) => (
                          <option key={s.id} value={s.id}>{s.subjectName}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveSubjectRow(index)}
                  disabled={newSubjects.length === 1 || addingSubject}
                  style={{
                    background: newSubjects.length === 1 ? '#e5e7eb' : '#ef4444', border: 'none', borderRadius: '8px',
                    width: '32px', height: '32px', cursor: newSubjects.length === 1 ? 'not-allowed' : 'pointer', color: 'white', flexShrink: 0,
                  }}
                >
                  <i className="pi pi-trash" style={{ fontSize: '12px' }} />
                </button>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleAddSubjectRow}
              disabled={addingSubject}
              style={{
                background: 'white', border: '2px solid #3b82f6', borderRadius: '12px', padding: '10px 20px',
                cursor: addingSubject ? 'not-allowed' : 'pointer', color: '#3b82f6', fontSize: '14px', fontWeight: '600',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}
            >
              <i className="pi pi-plus" /> Add Another
            </button>
            <button
              onClick={handleSaveNewSubjects}
              disabled={addingSubject || !currentSessionId}
              style={{
                background: addingSubject || !currentSessionId ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none',
                borderRadius: '12px', padding: '10px 24px', cursor: addingSubject || !currentSessionId ? 'not-allowed' : 'pointer', color: 'white',
                fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
              }}
            >
              {addingSubject ? 'Saving...' : 'Save Subjects'}
            </button>
          </div>
        </div>
      )}

      {/* Subjects List */}
      {selectedClassId ? (
        subjectsLoading ? (
          <div style={{ background: 'white', borderRadius: '20px', padding: '48px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '48px', color: '#2855ae' }} />
            <p style={{ color: '#6b7280', marginTop: '16px', fontSize: '16px', fontWeight: '500' }}>Loading subjects...</p>
          </div>
        ) : hasSubjects ? (
          <div style={{ background: 'white', borderRadius: '20px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', animation: 'fadeInSlide 0.7s ease-out' }}>

            {/* Header row: title, status filter, select toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: 10 }}>
              <h3 style={{ color: '#2855ae', fontSize: '16px', fontWeight: '600', margin: 0 }}>
                Subjects for {selectedClass!.className}
                <span style={{ color: '#94a3b8', fontWeight: '400', fontSize: '13px', marginLeft: '8px' }}>
                  ({activeCount} active, {inactiveCount} inactive)
                </span>
              </h3>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['all', 'active', 'inactive'] as StatusFilter[]).map((f) => (
                    <button
                      key={f}
                      className={`status-filter-chip ${statusFilter === f ? 'active-filter' : ''}`}
                      onClick={() => setStatusFilter(f)}
                    >
                      {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Inactive'}
                    </button>
                  ))}
                </div>
                <button className={`select-mode-btn ${selectionMode ? 'on' : 'off'}`} onClick={toggleSelectionMode}>
                  <i className="pi pi-check-square" style={{ fontSize: 12 }} />
                  {selectionMode ? 'Exit Select' : 'Select'}
                </button>
              </div>
            </div>

            {/* Bulk action bar */}
            {someSelected && (
              <div className="bulk-action-bar">
                <div className="bulk-count">
                  {selectedIds.size} subject{selectedIds.size !== 1 ? 's' : ''} selected
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="bulk-action-btn activate"
                    disabled={!anySelectedInactive || bulkActing}
                    onClick={() => handleBulkStatusChange('active')}
                  >
                    <i className="pi pi-check-circle" style={{ fontSize: 12 }} /> Activate
                  </button>
                  <button
                    className="bulk-action-btn deactivate"
                    disabled={!anySelectedActive || bulkActing}
                    onClick={() => handleBulkStatusChange('inactive')}
                  >
                    <i className="pi pi-ban" style={{ fontSize: 12 }} /> Deactivate
                  </button>
                  <button
                    className="bulk-action-btn primary"
                    onClick={() => openEditModal(selectedSubjects)}
                  >
                    <i className="pi pi-pencil" style={{ fontSize: 12 }} /> Edit
                  </button>
                  <button className="bulk-action-btn ghost" onClick={clearSelection}>
                    <i className="pi pi-times" style={{ fontSize: 12 }} /> Clear
                  </button>
                </div>
              </div>
            )}

            {selectionMode && visibleSubjects.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingLeft: 4 }}>
                <input
                  type="checkbox"
                  className="subject-row-checkbox"
                  checked={allSelected}
                  onChange={() => toggleSelectAll(visibleSubjects)}
                />
                <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Select all visible</span>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {visibleSubjects.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: 13 }}>
                  No {statusFilter} subjects for this class.
                </div>
              ) : (
                visibleSubjects.map((subject, index) => {
                  const colors = getTypeColor(subject.type);
                  const isChecked = selectedIds.has(subject.id);
                  const isActive = subject.status === 'active';
                  const isToggling = togglingId === subject.id;

                  return (
                    <div
                      key={subject.id}
                      style={{
                        background: isChecked ? '#eff6ff' : isActive ? '#f9fafb' : '#fef2f2',
                        borderRadius: '12px',
                        padding: '14px 18px',
                        border: isChecked ? '1.5px solid #93c5fd' : isActive ? '1px solid #e5e7eb' : '1px solid #fecaca',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        transition: 'all 0.15s',
                        opacity: isActive ? 1 : 0.85,
                      }}
                    >
                      {selectionMode && (
                        <input
                          type="checkbox"
                          className="subject-row-checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelect(subject.id)}
                        />
                      )}

                      <div style={{
                        minWidth: '36px', height: '36px',
                        background: isActive ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : '#9ca3af',
                        borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: '600', fontSize: '13px', flexShrink: 0,
                      }}>
                        {index + 1}
                      </div>

                      <div style={{ flex: 1, maxWidth: '320px' }}>
                        <InputText
                          value={subject.subjectName}
                          disabled
                          style={{
                            width: '100%', border: '1px solid #2855ae', borderRadius: '8px', padding: '9px 12px',
                            fontSize: '13px', background: '#f9fafb', color: '#1f2937', cursor: 'not-allowed',
                          }}
                        />
                      </div>

                      <span style={{
                        background: colors.bg, color: colors.text, padding: '4px 10px', borderRadius: '6px',
                        fontSize: '11px', fontWeight: '600', textTransform: 'capitalize', flexShrink: 0,
                      }}>
                        {subject.type}
                      </span>

                      {subject.type === 'combined' && subject.mainSubjectId && (
                        <span style={{ fontSize: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <i className="pi pi-arrow-up-right" style={{ fontSize: '10px', color: '#4338ca' }} />
                          <span style={{ color: '#4338ca', fontWeight: '600' }}>
                            {getMainSubjectName(subject.mainSubjectId, allClassSubjects) ?? `#${subject.mainSubjectId}`}
                          </span>
                        </span>
                      )}

                      {/* Inline status switch — direct activate/deactivate, no modal */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? '#16a34a' : '#dc2626', minWidth: 52, textAlign: 'right' }}>
                          {isToggling ? '...' : isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          className={`status-switch ${isActive ? 'on' : 'off'}`}
                          disabled={isToggling}
                          onClick={() => handleToggleStatus(subject)}
                          title={isActive ? 'Deactivate subject' : 'Activate subject'}
                        >
                          <span className="status-switch-knob" />
                        </button>
                      </div>

                      <button
                        onClick={() => openEditModal([subject])}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#3b82f6', transition: 'all 0.2s', padding: '4px', flexShrink: 0 }}
                        onMouseOver={(e) => { e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = '#3b82f6'; e.currentTarget.style.transform = 'scale(1)'; }}
                        title="Edit subject"
                      >
                        <i className="pi pi-pencil" style={{ fontSize: '13px' }} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '20px', padding: '48px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <i className="pi pi-inbox" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
            <h3 style={{ color: '#6b7280', fontSize: '18px', margin: '0 0 8px 0' }}>No Subjects Found</h3>
            <p style={{ color: '#9ca3af', fontSize: '14px', margin: '0 0 20px 0' }}>Add a subject above, or upload a CSV file to add subjects for this class.</p>
            <div className="custom-file-upload">
              <FileUpload
                ref={fileUploadRef}
                name="csv_file"
                accept=".csv"
                maxFileSize={5000000}
                customUpload
                uploadHandler={handleFileUpload}
                onSelect={handleFileSelect}
                onClear={() => { setFileSelected(false); setCsvPreview([]); }}
                onRemove={() => { setFileSelected(false); setCsvPreview([]); }}
                disabled={uploading}
                chooseLabel="Choose CSV File"
                uploadLabel="Upload Subjects"
                cancelLabel="Clear"
                emptyTemplate={<p style={{ color: '#6b7280', textAlign: 'center', padding: '10px', margin: 0, fontSize: '13px' }}>Drag and drop CSV file here to upload.</p>}
              />
            </div>
          </div>
        )
      ) : (
        <div style={{ background: 'white', borderRadius: '20px', padding: '48px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <i className="pi pi-inbox" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
          <h3 style={{ color: '#6b7280', fontSize: '18px', margin: '0 0 8px 0' }}>No Class Selected</h3>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>Select a class above to view and manage its subjects.</p>
        </div>
      )}

      {/* CSV Upload & Instructions */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginTop: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '2px solid #dbeafe' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', padding: '8px', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="pi pi-upload" style={{ fontSize: '14px', color: 'white' }} />
          </div>
          <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: '600', margin: '0' }}>Upload Subjects via CSV</h4>
        </div>

        <div className="custom-file-upload">
          <FileUpload
            ref={fileUploadRef}
            name="csv_file"
            accept=".csv"
            maxFileSize={5000000}
            customUpload
            uploadHandler={handleFileUpload}
            onSelect={handleFileSelect}
            onClear={() => { setFileSelected(false); setCsvPreview([]); }}
            onRemove={() => { setFileSelected(false); setCsvPreview([]); }}
            disabled={uploading}
            chooseLabel="Choose CSV File"
            uploadLabel="Upload Subjects"
            cancelLabel="Clear"
            emptyTemplate={<p style={{ color: '#6b7280', textAlign: 'center', padding: '10px', margin: 0, fontSize: '13px' }}>Drag and drop CSV file here to upload.</p>}
          />
        </div>

        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', padding: '8px', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="pi pi-info-circle" style={{ fontSize: '14px', color: 'white' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: '600', margin: '0 0 6px 0' }}>CSV Format Instructions</h4>
              <p style={{ color: '#6b7280', fontSize: '13px', margin: 0, lineHeight: '1.6' }}>
                Upload a CSV file with columns: <strong>class_name, subject_names</strong>.
                Subject names should be a comma-separated list of subjects for the class, wrapped in quotes
                (e.g. <code>4th,"Math, English, Science"</code>).
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={downloadSampleCSV}
              disabled={loadingAll}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none', borderRadius: '12px',
                padding: '10px 20px', cursor: loadingAll ? 'not-allowed' : 'pointer', color: 'white', fontSize: '14px',
                fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)', opacity: loadingAll ? 0.6 : 1,
              }}
            >
              {loadingAll ? <i className="pi pi-spin pi-spinner" style={{ fontSize: '14px' }} /> : <i className="pi pi-download" style={{ fontSize: '14px' }} />}
              {loadingAll ? 'Preparing CSV...' : 'Download Sample CSV'}
            </button>
          </div>
        </div>
      </div>

      {/* CSV Preview */}
      {csvPreview.length > 0 && (
        <div style={{ marginTop: '20px', background: '#f9fafb', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h4 style={{ color: '#2855ae', fontSize: '16px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="pi pi-eye" style={{ fontSize: '16px' }} />
              CSV Preview ({csvPreview.length} class(es), {csvPreview.reduce((sum, group) => sum + group.subjects.length, 0)} subject(s))
            </h4>
            <button
              onClick={() => { setCsvPreview([]); setFileSelected(false); if (fileUploadRef.current) fileUploadRef.current.clear(); }}
              style={{ background: '#ef4444', border: 'none', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', color: 'white', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <i className="pi pi-times" style={{ fontSize: '12px' }} />
              Clear Preview
            </button>
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {csvPreview.map((group) => (
              <div key={group.class_name} style={{ marginBottom: '12px' }}>
                <h5 style={{ color: '#2855ae', fontSize: '14px', fontWeight: '600', margin: '0 0 8px 0', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>
                  {group.class_name} ({group.subjects.length} subject(s))
                </h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {group.subjects.map((item, index) => {
                    const colors = getTypeColor(item.subject_type);
                    return (
                      <div key={`${group.class_name}-${index}`} style={{ background: 'white', borderRadius: '10px', padding: '12px 16px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '8px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '11px', fontWeight: '600', flexShrink: 0 }}>
                          {index + 1}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: '0', color: '#2855ae', fontSize: '13px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.subject_name}
                          </p>
                        </div>
                        <span style={{ background: colors.bg, color: colors.text, padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize', flexShrink: 0 }}>
                          {item.subject_type}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}